/** Structured combat log for run summary / debugging. */

export type LogKind = "lie" | "truth" | "strain" | "system" | "meta";

/** Board anchor for discourse popups (layer row + optional slot). */
export interface LogOrigin {
  layer: "mind" | "heart" | "will";
  /** 0–2; omit to anchor on the layer row center */
  slot?: number;
}

export interface CombatLogEntry {
  /** Monotonic index in this run */
  seq: number;
  /** Mission elapsed time (seconds) when logged */
  t: number;
  /** Wall-clock ISO timestamp */
  wall: string;
  kind: LogKind;
  message: string;
  /** Where on the board this was spoken / resolved */
  origin?: LogOrigin;
}

export interface RunStats {
  scenarioId: string;
  scenarioName: string;
  difficulty: string;
  status: "playing" | "won" | "lost";
  /** Elapsed mission time at end */
  elapsed: number;
  duration: number;
  versesPlayed: number;
  placeablesPlayed: number;
  instantsPlayed: number;
  paidDraws: number;
  reveals: number;
  liesSpawned: number;
  liesSilenced: number;
  lieLevelDrops: number;
  prayersPlaced: number;
  prayersBroken: number;
  redeems: number;
  minIntegrity: number;
  integrityEnd: number;
  deckPoolSize: number;
  /** Verse def ids played this run (order, with duplicates) */
  versePlayOrder: string[];
}

export function classifyLogMessage(msg: string): LogKind {
  const m = msg.trim();
  if (/^Truth:/.test(m)) return "truth";
  if (
    /\babides\b/.test(m) ||
    /\bfell silent\b/.test(m) ||
    /\bfalls silent\b/.test(m) ||
    /\baccusation interrupted\b/.test(m)
  ) {
    return "truth";
  }
  if (/^[1-3]?[A-Za-z. ]+\d+:\d/.test(m) && /[“"]/.test(m)) return "truth";
  if (
    /\bstrain each other\b/.test(m) ||
    /\bcrowd each other\b/.test(m) ||
    /\bis packed\b/.test(m) ||
    /\bstrained\b/.test(m) ||
    (/\bpresses\b/.test(m) && !/\bpresses into\b/.test(m))
  ) {
    return "strain";
  }
  if (
    /\benters the\b/.test(m) ||
    /\bpresses into\b/.test(m) ||
    /\bbreaches\b/.test(m) ||
    /\bhammers the\b/.test(m) ||
    /\baccuses the Secret Place\b/.test(m) ||
    /\bmultiplies\b/.test(m) ||
    /→ “/.test(m) ||
    /\):\s*“/.test(m) ||
    /\b: “/.test(m)
  ) {
    return "lie";
  }
  if (/^\[meta\]/i.test(m) || /^Mission/.test(m) || /^Difficulty/.test(m)) {
    return "meta";
  }
  return "system";
}

/** Format mission time as m:ss.d */
export function formatMissionTime(t: number): string {
  const clamped = Math.max(0, t);
  const m = Math.floor(clamped / 60);
  const s = clamped - m * 60;
  const whole = Math.floor(s);
  const tenths = Math.floor((s - whole) * 10);
  return `${m}:${whole.toString().padStart(2, "0")}.${tenths}`;
}

/** Plain-text dump for copy/debug */
function formatOrigin(o: LogOrigin | undefined): string {
  if (!o) return "";
  const slot = o.slot !== undefined ? `:${o.slot + 1}` : "";
  return ` @${o.layer}${slot}`;
}

export function formatCombatLogText(entries: CombatLogEntry[]): string {
  return entries
    .map(
      (e) =>
        `[${formatMissionTime(e.t)}] #${e.seq} [${e.kind}]${formatOrigin(e.origin)} ${e.message}  (${e.wall})`,
    )
    .join("\n");
}

export function emptyRunStats(
  partial: Pick<
    RunStats,
    "scenarioId" | "scenarioName" | "difficulty" | "duration" | "deckPoolSize"
  >,
): RunStats {
  return {
    ...partial,
    status: "playing",
    elapsed: 0,
    versesPlayed: 0,
    placeablesPlayed: 0,
    instantsPlayed: 0,
    paidDraws: 0,
    reveals: 0,
    liesSpawned: 0,
    liesSilenced: 0,
    lieLevelDrops: 0,
    prayersPlaced: 0,
    prayersBroken: 0,
    redeems: 0,
    minIntegrity: 100,
    integrityEnd: 100,
    versePlayOrder: [],
  };
}
